import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { useDispatch, useSelector } from "react-redux";
import {
  selectMembershipDetails,
  selectMemberInvoices,
  setMembership,
} from "../../../../store/slices/membershipSlice";
import { defaultPaidById, useAccountPeople } from "../../../membership/BillingHistory/SharedAccountBilling";
import { useLeadBillingPrompt, withLeadBillingChoice } from "../../../../hooks/useLeadBillingPrompt";
import {
  acceptPayment,
  getStripePaymentMethods,
  getAllPaymentMethods,
} from "../../../../api/paymentsAPI";
import SelectAdvancePaymentModalByMember from "./SelectAdvancePaymentModalByMember";
import { getMemberById } from "../../../../api/membershipAPI";
import { toast } from "react-toastify";
import { getTenantId } from "../../../../api/apiHelpers";
import { useTerminalReaders } from "../../../../hooks/useTerminalReaders";
import { useReaderWait } from "../../../../hooks/useReaderWait";
import ReaderWaitPanel from "../../../payment/reader/ReaderWaitPanel";
import { describeReaderOutcome } from "../../../../utils/readerCharge";
import { newAttemptKey } from "../../../../utils/attemptKey";
import { defaultMethodIdFrom } from "../../../../utils/savedPaymentMethods";
import { staffCardRefusalMessage } from "../../../../utils/paymentErrors";

// The attempt key lives in utils/attemptKey (shared with the POS and the
// enrollment wizard) — see that file for why one key spans a double-click.

interface MyComponentProps {
  refreshApiCalls: number;
  setRefreshApiCalls: Dispatch<SetStateAction<number>>;
  showPaymentModal: boolean;
  setShowPaymentModal: Dispatch<SetStateAction<boolean>>;
}

const SelectAdvancePaymentMethodInMember: React.FC<MyComponentProps> = ({
  refreshApiCalls,
  setRefreshApiCalls,
  showPaymentModal,
  setShowPaymentModal,
}: any) => {
  // member Ship
  const membership: any = useSelector(selectMembershipDetails);
  // A lead's account: one prompt before a payment is taken (never blocks).
  // Once staff convert from the prompt, refresh this screen's copy of the account.
  const { confirmLeadBilling, leadBillingPrompt } = useLeadBillingPrompt({
    onConverted: () => { void fetchMembershipDetails(); },
  });

  // Loadinf & Error states
  const [, setLoading] = useState<boolean>(true); // Loading state
  const userDetails = JSON?.parse(localStorage.getItem("userDetails") || "{}");

  // Stripe States
  const [stripeCustomerId, setStripeCustomerId] = useState<any>("");
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<
    string | null
  >(null);
  const dispatch = useDispatch();
  // store default card id
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [savedBankAccounts, setSavedBankAccounts] = useState<any[]>([]);
  // HELPER
  const token = localStorage.getItem("authToken");
  const localTenantId = getTenantId() || "";

  // Shared accounts: the people who can be named as "Paid by", and the
  // family's invoices (already loaded by the billing table) to start the
  // field on the responsible person.
  const accountPeople = useAccountPeople(token, membership?.id);
  const memberInvoices: any = useSelector(selectMemberInvoices);

  // Card reader (Stripe Terminal): which reader, whether to keep the card,
  // and the shared "waiting for the card…" state.
  const terminal = useTerminalReaders(token);
  const [readerId, setReaderId] = useState<string | null>(null);
  const [saveCardOnReader, setSaveCardOnReader] = useState(false);
  const readerWait = useReaderWait(token);
  useEffect(() => {
    if (!readerId && terminal.readers.length > 0) setReaderId(terminal.readers[0].id);
  }, [terminal.readers, readerId]);

  /**
   * Set while an attempt is in flight, cleared when it settles.
   *
   * Held for the DURATION of one attempt, not for the life of the modal: a
   * second click while the request is outstanding reuses the key and Stripe
   * returns the first charge, but a retry after a failure gets a fresh one.
   * That distinction matters — Stripe rejects a reused key whose parameters
   * changed, so a permanent key would turn "card declined, try $50 instead"
   * into an idempotency error.
   */
  const attemptKeyRef = useRef<string | null>(null);

  /** The bank account the modal shows for an ACH payment, and charges. */
  const bankAccountForPayment = () =>
    savedBankAccounts?.find((ba) => ba?.id === defaultPaymentMethodId) || savedBankAccounts?.[0];

  const handlePaymentSubmit = async ({
    method,
    amount,
    useSavedCard,
    newCardPaymentMethodId,
    checkDetails,
    note,
    receiptNumber,
    paymentDate, // ← Add payment date parameter
    paidByMembershipUserId,
    readerId: chosenReaderId,
    saveCardOnReader: wantsSavedCard,
  }: any) => {
    // A lead's account: ask once before the payment is taken. Going back
    // returns false, which leaves the payment form open with what staff typed.
    const leadDecision = await confirmLeadBilling({
      membershipId: membership?.id,
      accountType: membership?.account_type,
      familyName: `${membership?.first_name || ""} ${membership?.last_name || ""}`.trim(),
      action: "payment",
    });
    if (!leadDecision.proceed) return false;

    try {
      // First click mints the key; a second click while that request is still
      // outstanding finds it already set and sends the same one, which is what
      // makes the two collapse into one charge at Stripe.
      if (!attemptKeyRef.current) {
        attemptKeyRef.current = newAttemptKey();
      }

      // Use the new card's payment method ID if provided, otherwise
      // fall back to the saved default payment method.
      //
      // A bank (ACH) payment uses the bank account the modal SHOWS
      // ("Use saved bank account ... 1234"). It used to send the default
      // method or the selected card instead, so a bank payment was charged
      // to a card (2026-10-10).
      const effectivePaymentMethodId =
        method === "us_bank_account"
          ? bankAccountForPayment()?.id || null
          : newCardPaymentMethodId || defaultPaymentMethodId;

      const paymentObj = {
        amount: amount, // amount
        method: method, // method
        paymentMethodId: effectivePaymentMethodId,
        saveCard: method === "card_reader" ? !!wantsSavedCard : useSavedCard,
        readerId: method === "card_reader" ? chosenReaderId || readerId : undefined,
        checkDetails: checkDetails, // checkDetails
        tenantId: localTenantId, // tenant
        membershipId: membership?.id, // member
        note: note,
        receiptNumber: receiptNumber, // 👈 pass receipt number,
        paymentDate: paymentDate, // ← Add payment date
        // Shared accounts: who paid. The server checks the person is on the
        // account and drops anything else.
        paid_by_membership_user_id: paidByMembershipUserId || undefined,
        // Stable across a double-click, new on a retry. See attemptKeyRef.
        idempotencyKey: attemptKeyRef.current,
        processed_by: {
          userId: userDetails?.id,
          name: userDetails?.first_name + " " + userDetails?.last_name,
          role: userDetails?.role,
        },
      };

      const res: any = await acceptPayment(token, withLeadBillingChoice(paymentObj, leadDecision));

      // Card reader: the backend answered 202 — the money is taken on the
      // reader. Show the wait, poll, and only then report success.
      if (
        res?.data?.success === true &&
        res?.data?.data?.status === "waiting_for_reader" &&
        res?.data?.data?.paymentIntentId
      ) {
        const rid = res.data.data.readerId || chosenReaderId || readerId || "";
        const reader = terminal.readers.find((r) => r.id === rid);
        const outcome = await readerWait.run({
          paymentIntentId: res.data.data.paymentIntentId,
          readerId: rid,
          readerLabel: reader?.label,
          simulated: !!reader?.simulated,
          amount: Number(amount) + Number(res.data.data.surchargeAmount || 0),
        });
        if (outcome.outcome === "succeeded") {
          toast.success(describeReaderOutcome(outcome));
          setRefreshApiCalls((prev: number) => prev + 1);
          fetchMembershipDetails();
          return true;
        }
        toast.error(
          describeReaderOutcome(outcome),
          outcome.outcome === "charged_not_recorded" ? { autoClose: false } : undefined
        );
        return false;
      }

      // Check if response is undefined (API error)
      if (!res) {
        toast.error("Server error. Please try again or contact support.");
        return false;
      }

      if (res?.data?.success === true) {
        // Check payment status
        const paymentStatus = res?.data?.data?.status;

        if (paymentStatus === "scheduled") {
          const scheduledDate = res?.data?.data?.scheduledDate || paymentDate;
          toast.success(
            `Payment scheduled for ${formatDateOnly(scheduledDate)}!`
          );
        } else if (paymentStatus === "processing") {
          // ACH bank payments return 'processing' status - they take 3-5 business days
          toast.success(
            "Bank payment submitted! ACH transfers typically take 3-5 business days to complete."
          );
        } else {
          toast.success("Payment accepted successfully!");
        }
        // The payment was recorded but something about it needs staff: today,
        // a Receipt # that was already used, so it was saved without one. The
        // server has always said so; this screen never showed it.
        if (typeof res?.data?.warning === "string" && res.data.warning.trim()) {
          toast.warning(res.data.warning, { autoClose: false });
        }
        setRefreshApiCalls((prev: number) => prev + 1);
        fetchMembershipDetails();
        return true;
      } else {
        // acceptPayment returns TWO different shapes: an axios response on
        // success (message/error live under .data) and, from its catch, a flat
        // { success: false, error } with no .data at all. Reading only the
        // nested form meant every failure fell through to the generic string
        // below while the backend's actual reason was sitting on res.error.
        //
        // That is how "That receipt number is already used. Enter a different
        // receipt number and try again." never reached anyone: staff type their
        // initials into the receipt field, the unique index rejected it, and
        // the screen said "Failed to accept payment" for six months.
        //
        // Two refusals have their own plain words, the same on every staff
        // screen: a card whose bank wants the cardholder to approve the
        // payment (this window cannot do that; it said only "Failed to accept
        // payment"), and a club with no card processor. Neither is fixed by
        // trying again, so the message stays up until staff close it.
        const refusal = staffCardRefusalMessage(res?.data);
        if (refusal) {
          toast.error(refusal, { autoClose: false });
          return false;
        }
        const errorMessage =
          res?.data?.message ||
          res?.data?.error ||
          res?.error ||
          "Failed to accept payment";
        toast.error(errorMessage);
        return false;
      }
    } catch (error: any) {
      const { getPaymentErrorMessage } = await import("../../../../utils/paymentErrors");
      toast.error(getPaymentErrorMessage(error));
      return false;
    } finally {
      // The attempt has settled either way, so the next submit is a NEW
      // attempt and must not reuse this key — see attemptKeyRef.
      attemptKeyRef.current = null;
    }
  };

  const fetchMembershipDetails = async () => {
    try {
      const response = await getMemberById(token, membership.id);

      // Assuming the membership details have a field `id`
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
      // Error handled by axios interceptor (Sentry)
    }
  };
  // Get Stripe customer ID from membership
  useEffect(() => {
    if (membership?.stripe_customer_id) {
      setStripeCustomerId(membership.stripe_customer_id);
    }
    setLoading(false);
  }, [membership?.id, membership?.stripe_customer_id]);

  // Fetch saved payment methods (cards and bank accounts) when stripeCustomerId changes
  useEffect(() => {
    const fetchSavedPaymentMethods = async () => {
      if (!stripeCustomerId) {
        return;
      }

      try {
        // Try to use getAllPaymentMethods first
        const response = await getAllPaymentMethods(token, stripeCustomerId);

        if (response?.success) {
          // API returns cards in data or cards field, not paymentMethods
          const allMethods = response?.data || response?.cards || [];
          const cards = allMethods.filter((pm: any) => pm.type === 'card');
          const bankAccounts = allMethods.filter((pm: any) => pm.type === 'us_bank_account');

          setSavedCards(cards);
          setSavedBankAccounts(bankAccounts);

          // Set default payment method
          setDefaultPaymentMethodId(response?.defaultPaymentMethodId || allMethods?.[0]?.id || null);
        } else {
          // Fallback to old API
          const fallbackResponse = await getStripePaymentMethods(token, stripeCustomerId);
          setSavedCards(fallbackResponse?.data || []);
          setSavedBankAccounts([]);

          const fallbackDefaultId = defaultMethodIdFrom(fallbackResponse);
          if (fallbackDefaultId) {
            setDefaultPaymentMethodId(fallbackDefaultId);
          } else {
            setDefaultPaymentMethodId(fallbackResponse?.data?.[0]?.id || null);
          }
        }
      } catch (error) {
        // Error handled by axios interceptor (Sentry)
      }
    };

    if (stripeCustomerId) {
      fetchSavedPaymentMethods();
    }
  }, [stripeCustomerId]);

  // If the membership in Redux doesn't carry a balance yet (e.g. a slim
  // list-row object was dispatched), fetch full details the same way the
  // post-payment refresh does so the amount-due prefill has data.
  useEffect(() => {
    if (showPaymentModal && membership?.id && membership?.balance === undefined) {
      fetchMembershipDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, membership?.id, membership?.balance]);

  // Find default card and bank account
  const defaultCard = savedCards?.find((card) => card?.id === defaultPaymentMethodId) || savedCards?.[0];
  const defaultBankAccount = bankAccountForPayment();

  // Amount due for the prefill. Sign convention (see BalanceDisplay /
  // NegativeBalances / reactivateMembership): balance < 0 means the member
  // OWES money, balance >= 0 is credit. So amount due = -balance when
  // negative, else 0 (no prefill).
  const balanceNum = Number(membership?.balance);
  const amountDue =
    Number.isFinite(balanceNum) && balanceNum < 0 ? Math.abs(balanceNum) : 0;

  return (
    <>
      <SelectAdvancePaymentModalByMember
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handlePaymentSubmit}
        savedCardLast4={defaultCard?.card?.last4}
        savedCards={savedCards}
        defaultPaymentMethodId={defaultPaymentMethodId}
        onCardSelect={(cardId: string) => setDefaultPaymentMethodId(cardId)}
        savedBankAccountLast4={defaultBankAccount?.us_bank_account?.last4}
        savedBankAccountName={defaultBankAccount?.us_bank_account?.bank_name}
        defaultAmount={amountDue}
        readers={terminal.enabled ? terminal.readers : []}
        readerId={readerId}
        onReaderSelect={setReaderId}
        saveCardOnReader={saveCardOnReader}
        onSaveCardOnReaderChange={setSaveCardOnReader}
        people={accountPeople}
        defaultPaidById={defaultPaidById(memberInvoices, accountPeople)}
        fetch={() => {}}
        membershipId={membership?.id}
      />
      {readerWait.readerWait && (
        <ReaderWaitPanel
          wait={readerWait.readerWait}
          onCancel={readerWait.cancel}
          onSimulateTap={readerWait.simulateTap}
        />
      )}
      {leadBillingPrompt}
    </>
  );
};

export default SelectAdvancePaymentMethodInMember;
